import { useState, useEffect } from "react";
import { HardDrive, AlertTriangle, Trash2, RefreshCw, ShieldCheck } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import {
  getOrphanReport, purgeOrphanRecordings, saveTelephonySettings, getTelephonySettings,
  type OrphanReport, type OrphanPurgeResult, type TelephonyStatus,
} from "../../../../lib/calls";
import { Btn, StatusPill } from "../../../ui";
import { ConfirmModal } from "../../_shared/ConfirmModal";
import { IntegrationCard } from "../panel/IntegrationCard";
import { Note, SaveRow, ToggleRow } from "../panel/fields";
import { RetentionFields } from "./RetentionFields";
import { UnknownRecordingsPanel } from "./UnknownRecordingsPanel";

/**
 * Settings -> Integrations: what Twilio still stores for us, and the cleanup.
 *
 * Separate from TelephonyIntegrationCard on purpose: that card is credentials and
 * call behaviour, this one is a recurring bill. Twilio charges recording storage
 * per minute per month, and our own download deletes Twilio's copy only on a
 * best-effort basis, so failures pile up unnoticed.
 *
 * The scan never deletes. Deleting is a separate, explicit button behind a
 * confirmation, and the background job that does it on a schedule stays off
 * until switched on here.
 */
export function RecordingStorageCard() {
  const t = useT();
  const [report, setReport] = useState<OrphanReport | null>(null);
  const [scanning, setScanning] = useState(false);
  const [purging, setPurging] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [purged, setPurged] = useState<OrphanPurgeResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [purgeEnabled, setPurgeEnabled] = useState(false);
  const [showUnknown, setShowUnknown] = useState(false);
  const [status, setStatus] = useState<TelephonyStatus | null>(null);

  // Only the settings are loaded up front. The scan itself is a Twilio round
  // trip, so it waits for the operator to ask for it.
  //
  // The whole answer is kept now, not just the one switch: the retention block
  // below reads its three fields and its due counts from the same response,
  // which is why this card fetches once rather than twice.
  useEffect(() => {
    getTelephonySettings()
      .then((s) => {
        setStatus(s);
        setPurgeEnabled(s.twilio.purgeOrphanRecordings);
      })
      .catch(() => {});
  }, []);

  async function handleScan() {
    setScanning(true);
    setError(null);
    setPurged(null);
    try {
      // verify: check each copy against our storage. Slower, but the deletable
      // count is only trustworthy when the object was actually seen.
      setReport(await getOrphanReport({ verify: true }));
    } catch {
      setError(t("settings.recordingStorage.scanFailed"));
    } finally {
      setScanning(false);
    }
  }

  async function handlePurge() {
    setPurging(true);
    setError(null);
    try {
      const result = await purgeOrphanRecordings();
      setPurged(result);
      setReport(await getOrphanReport({ verify: true }));
    } catch {
      setError(t("settings.recordingStorage.purgeFailed"));
    } finally {
      setPurging(false);
      setConfirming(false);
    }
  }

  async function handleToggle(next: boolean) {
    setPurgeEnabled(next);
    try {
      await saveTelephonySettings({ twilio: { purgeOrphanRecordings: next } });
    } catch {
      setPurgeEnabled(!next); // Put the switch back where the server still has it.
      setError(t("settings.recordingStorage.toggleFailed"));
    }
  }

  const redundant = report?.byKind.redundant.count ?? 0;
  const missing = report?.byKind.missing_local.count ?? 0;
  const staleFailed = report?.byKind.stale_failed.count ?? 0;

  return (
    <div className="space-y-4">
      <IntegrationCard
        icon={HardDrive}
        title={t("settings.recordingStorage.title")}
        desc={t("settings.recordingStorage.desc")}
        status={
          report ? (
            // Warning, not destructive: money leaking every month is worth a
            // colour, but nothing here is broken and the strong tone belongs to
            // the recordings we can no longer replace.
            <StatusPill tone={redundant > 0 ? "wt" : "ok"}>
              {redundant > 0
                ? t("settings.recordingStorage.badgeWaste", { usd: report.redundantMonthlyUsd.toFixed(2) })
                : t("settings.recordingStorage.badgeClean")}
            </StatusPill>
          ) : null
        }
      >
        <div className="space-y-4">
          {report ? (
            <>
              <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
                <Stat
                  label={t("settings.recordingStorage.kind.redundant")}
                  value={redundant}
                  minutes={report.byKind.redundant.minutes}
                  tone={redundant > 0 ? "warn" : "ok"}
                />
                <Stat
                  label={t("settings.recordingStorage.kind.in_flight")}
                  value={report.byKind.in_flight.count}
                  minutes={report.byKind.in_flight.minutes}
                  tone="plain"
                />
                {/* Its own tile rather than a share of "in progress", which is
                    where these used to hide. Nothing is in progress: our side
                    gave up and Twilio still holds the audio, so every one of
                    these is a recording the call pane's retry can bring back. A
                    warning tone, because a number that stays above zero is a
                    setting somebody has not fixed yet. */}
                <Stat
                  label={t("settings.recordingStorage.kind.stale_failed")}
                  value={staleFailed}
                  minutes={report.byKind.stale_failed.minutes}
                  tone={staleFailed > 0 ? "warn" : "plain"}
                />
                <Stat
                  label={t("settings.recordingStorage.kind.missing_local")}
                  value={missing}
                  minutes={report.byKind.missing_local.minutes}
                  tone={missing > 0 ? "bad" : "plain"}
                />
                <Stat
                  label={t("settings.recordingStorage.kind.unknown")}
                  value={report.byKind.unknown.count}
                  minutes={report.byKind.unknown.minutes}
                  tone="plain"
                />
              </div>

              <p className="crm-sub !mt-0 text-[12.75px]">
                {t("settings.recordingStorage.summary", {
                  scanned: report.scanned,
                  total: report.totalMonthlyUsd.toFixed(2),
                  waste: report.redundantMonthlyUsd.toFixed(2),
                })}
              </p>

              {report.truncated ? <Note tone="mut">{t("settings.recordingStorage.truncated")}</Note> : null}

              {missing > 0 ? (
                <Note tone="bad" icon={<AlertTriangle className="mt-0.5 size-4 shrink-0" />}>
                  {t("settings.recordingStorage.missingWarning", { count: missing })}
                </Note>
              ) : null}

              {report.byKind.unknown.count > 0 ? (
                <Note tone="mut">
                  <span className="flex flex-wrap items-center gap-3">
                    <span className="min-w-0 flex-1">
                      {t("settings.recordingStorage.unknownNote", { count: report.byKind.unknown.count })}
                    </span>
                    <Btn small onClick={() => setShowUnknown((v) => !v)}>
                      {showUnknown
                        ? t("settings.recordingStorage.unknown.hide")
                        : t("settings.recordingStorage.unknown.show")}
                    </Btn>
                  </span>
                </Note>
              ) : null}
            </>
          ) : null}

          {purged ? (
            <Note tone="ok" icon={<ShieldCheck className="mt-0.5 size-4 shrink-0" />} live>
              {t("settings.recordingStorage.purged", {
                deleted: purged.deleted,
                usd: purged.savedMonthlyUsd.toFixed(2),
                failed: purged.failed,
                remaining: purged.remaining,
              })}
            </Note>
          ) : null}

          {error ? <Note tone="bad" live>{error}</Note> : null}

          <SaveRow>
            <Btn onClick={handleScan} disabled={scanning || purging}>
              <RefreshCw className={scanning ? "size-3.5 animate-spin" : "size-3.5"} />
              {scanning ? t("settings.recordingStorage.scanning") : t("settings.recordingStorage.scan")}
            </Btn>
            <Btn
              tone="danger"
              onClick={() => setConfirming(true)}
              disabled={purging || scanning || redundant === 0}
            >
              {purging ? (
                <RefreshCw className="size-3.5 animate-spin" />
              ) : (
                <Trash2 className="size-3.5" />
              )}
              {purging
                ? t("settings.recordingStorage.purging")
                : t("settings.recordingStorage.purge", { count: redundant })}
            </Btn>
          </SaveRow>

          {/* The schedule is not one of the buttons above it: those act once,
              on demand, and this one decides what happens while nobody is
              looking. Its own line, with the sentence that says so. */}
          <ToggleRow
            label={t("settings.recordingStorage.autoPurge")}
            hint={t("settings.recordingStorage.autoPurgeHint")}
            checked={purgeEnabled}
            onChange={handleToggle}
          />

          {/* Retention is a different question from everything above it - that
              is about copies Twilio still bills us for, this is how long a
              conversation may be kept at all - so it gets a rule and a group
              title of its own rather than a fifth control in the same run. */}
          <div className="border-t border-[var(--crm-hair)] pt-5">
            <RetentionFields status={status} onSaved={setStatus} />
          </div>
        </div>
      </IntegrationCard>

      {/* A sibling of the card, not a child of it: the panel is a `crm-card` of
          its own and one card inside another reads as a mistake. */}
      {showUnknown ? <UnknownRecordingsPanel onDeleted={() => void handleScan()} /> : null}

      {confirming ? (
        <ConfirmModal
          title={t("settings.recordingStorage.confirmPurgeTitle")}
          body={t("settings.recordingStorage.confirmPurgeBody", { count: redundant })}
          confirmLabel={t("settings.recordingStorage.confirmPurgeYes")}
          cancelLabel={t("common.cancel")}
          onConfirm={handlePurge}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}

const STAT_COLOR: Record<string, string | undefined> = {
  ok: "var(--color-success)",
  warn: "color-mix(in oklab, var(--color-warning) 60%, var(--color-foreground))",
  bad: "var(--color-destructive)",
  plain: undefined,
};

/** One count out of the scan: how many, and how many minutes Twilio bills for
 *  them. The tile is sunken rather than bordered - the CRM has no boxes. */
function Stat({
  label,
  value,
  minutes,
  tone,
}: {
  label: string;
  value: number;
  minutes: number;
  tone: keyof typeof STAT_COLOR;
}) {
  const t = useT();
  return (
    <div className="rounded-[20px] bg-[var(--crm-sunk)] px-4 py-3.5">
      <div className="crm-tnum text-[26px] font-bold leading-none" style={{ color: STAT_COLOR[tone] }}>
        {value}
      </div>
      <div className="mt-2 text-[12px] font-[650]">{label}</div>
      <div className="crm-mut crm-tnum text-[11.5px]">
        {t("settings.recordingStorage.minutes", { minutes })}
      </div>
    </div>
  );
}
